/* eslint-disable class-methods-use-this,react/static-property-placement */
import has from 'lodash/fp/has';
import { createLink, prependHost } from './utils';
import {
  RUNNING,
  STOPPED,
  NAME,
  HOST,
  DEFAULT_MANIFEST,
  MOUNT,
  UNMOUNT,
  ENTRYPOINTS,
  FILE_EXTENSIONS,
} from './constants';
import MFEContext from './MFEContext';

declare let SystemJS: any;

export default class Application {
  static defaultConfig = {
    name: undefined,
    host: undefined,
    main: 'main.js',
    private: false,
    paths: [],
    disabled: false,
  };

  static defaultManifestFileName: string = DEFAULT_MANIFEST;

  static requiredConfigKeys: string[] = [NAME, HOST];

  static requiredModuleHooks: string[] = [MOUNT, UNMOUNT];

  config: AppConfig;

  validator: AppValidator;

  context: MFEContext;

  state: string;

  loaded: boolean;

  manifest?: AppManifest;

  module?: AppModule;

  constructor(config: any, validator: AppValidator, context: MFEContext) {
    this.validateConfig(config);
    this.config = {
      ...Application.defaultConfig,
      ...config,
    };
    this.validator = validator;
    this.context = context;
    this.state = STOPPED;
    this.loaded = false;
  }

  get host(): string {
    const { host } = this.config;
    return host.endsWith('/') ? host.slice(0, -1) : host;
  }

  get name(): string {
    return this.config.name;
  }

  get isPrivate(): boolean {
    return this.config.private;
  }

  get disabled(): boolean {
    return this.config.disabled;
  }

  set disabled(val: boolean) {
    this.config.disabled = val;
  }

  get entrypoints(): string[] | undefined {
    return this.manifest ? this.manifest.entrypoints : undefined;
  }

  hasManifest(): boolean {
    return Boolean(this.manifest);
  }

  async importManifest(
    manifest = Application.defaultManifestFileName,
    host = this.host
  ) {
    if (!this.hasManifest()) {
      try {
        const response = await fetch(`${host}/${manifest}`);
        const data: AppManifest = await response.json();
        if (!has(ENTRYPOINTS, data)) {
          throw new Error(
            `The manifest does not contain a key of ${ENTRYPOINTS}.`
          );
        }
        this.manifest = data;
      } catch (err) {
        return Promise.reject(err);
      }
    }
    return Promise.resolve();
  }

  async run() {
    if (this.disabled) {
      throw new Error(
        `You are attempting to run an application (${this.name}) that has been disabled.`
      );
    }

    if (this.isRunning()) {
      return Promise.resolve();
    }

    try {
      await this.importManifest();
      await this.load();
    } catch (err) {
      return Promise.reject(err);
    }

    this.validateModule(this.module);

    const appElement = document.getElementById(this.name);
    if (appElement && this.module) {
      this.appendStyles();
      this.module.mount(this.context, this.name);
      this.state = RUNNING;
    }

    return this;
  }

  stop() {
    if (this.isStopped() || !this.module) {
      return;
    }
    this.validateModule(this.module);
    this.module.unmount();
    this.state = STOPPED;
  }

  async load() {
    if (!this.hasManifest()) {
      await this.importManifest();
    }
    if (!this.loaded && this.entrypoints) {
      const entryJS = this.entrypoints.filter((file) =>
        file.endsWith(FILE_EXTENSIONS.JS)
      );
      const mainJS = entryJS.pop();
      if (mainJS) {
        await this.importFiles(entryJS);
        const module = await this.importFile(mainJS);
        this.validateModule(module);
        this.module = module as AppModule;
        this.loaded = true;
      }
    }
    return Promise.resolve();
  }

  isRunning(): boolean {
    return this.state === RUNNING;
  }

  isStopped(): boolean {
    return this.state === STOPPED;
  }

  appendStyles(): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.entrypoints &&
      this.entrypoints
        .filter((file) => file.endsWith(FILE_EXTENSIONS.CSS))
        .filter(
          (stylesheet) => stylesheet !== undefined && stylesheet.length > 0
        )
        .map((path) => prependHost(this.host, path))
        .map((stylesheet) => createLink(stylesheet))
        .forEach(($link) => document.head.appendChild($link));
  }

  async importFile(path: string): Promise<Record<string, unknown>> {
    const normalizedPath = prependHost(this.host, path);
    return SystemJS.import(normalizedPath);
  }

  async importFiles(paths: string[]): Promise<Record<string, unknown>[]> {
    return Promise.all(paths.map(async (path) => this.importFile(path)));
  }

  isValidRoute(location: Location = window.location) {
    const hasPaths = this.config.paths && this.config.paths.length;
    const { pathname } = location;
    return hasPaths
      ? this.validator(encodeURI(pathname), this.config.paths)
      : true;
  }

  validateConfig(config: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    config &&
      Application.requiredConfigKeys.forEach((key) => {
        if (!has(key, config)) {
          throw new Error(
            `Configuration for '${config.name}' is missing key '${key}'.`
          );
        }
      });
  }

  validateModule(module: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    module &&
      Application.requiredModuleHooks.forEach((key) => {
        if (!has(key, module)) {
          throw new Error(
            `The ${this.name} module needs to contain an exported ${MOUNT} and ${UNMOUNT} function.`
          );
        }
      });
  }
}
